import { ForwardedRef, forwardRef, SyntheticEvent, useCallback, useRef, useState } from 'react';
import { SingleDatePicker } from 'react-dates';
import { IconCross } from '@components/icons';
import { Props } from '@components/input-date/src/input-date.types';
import classNames from 'classnames';
import moment from 'moment-timezone';

// https://react-hook-form.com/api/usecontroller/controller https://react-hook-form.com/get-started#IntegratingwithUIlibraries
export const InputDate = forwardRef(
  (
    {
      value,
      required = true,
      onChange,
      hasError,
      locale,
      minBookingDate,
      maxBookingDate,
      readOnly,
      timeZone,
      showClearDate,
      ...props
    }: Props,
    _: ForwardedRef<HTMLInputElement>
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    const handleDateChange = useCallback(
      (date: moment.Moment | null) => {
        onChange?.(date);
      },
      [onChange]
    );

    const onFocusChangeHandler = ({ focused }: { focused: boolean }) => {
      if (!readOnly && !props.disabled) setIsFocused(focused);
    };

    const onMouseDownHandler = (e: SyntheticEvent) => {
      e.preventDefault();
      if (!readOnly && !props.disabled) setIsFocused(true);
    };

    const defaultProps = {
      // example props for the demo
      // autoFocus: false,
      // initialDate: value,
      // input related props
      id: props.id ?? 'date',
      placeholder: props.placeholder,
      disabled: props.disabled ?? false,
      readOnly: props.readonly ?? false,
      required,
      screenReaderInputMessage: '',
      showClearDate: false,
      showDefaultInputIcon: false,
      customInputIcon: null,
      block: true,
      small: false,
      regular: true,
      verticalSpacing: undefined,
      keepFocusOnInput: false,
      // autoComplete: 'off',
      // calendar presentation and interaction related props
      openDirection: props.openDirection ?? 'down',
      renderMonthText: null,
      // orientation: HORIZONTAL_ORIENTATION,
      // anchorDirection: ANCHOR_LEFT,
      horizontalMargin: 0,
      withPortal: false,
      withFullScreenPortal: false,
      initialVisibleMonth: props.initialVisibleMonth
        ? props.initialVisibleMonth
        : minBookingDate
          ? () => minBookingDate
          : null, // if initial date show current selectableMonth
      numberOfMonths: 1,
      keepOpenOnDateSelect: false,
      reopenPickerOnClearDate: false,
      isRTL: false,

      // navigation related props
      navPrev: null,
      navNext: null,
      onPrevMonthClick() {},
      onNextMonthClick() {},
      onClose() {},
      noBorder: true,

      // day presentation and interaction related props
      // renderCalendarDay: undefined,
      renderDayContents: null,
      enableOutsideDays: true,
      // isDayHighlighted: () => {},
      displayFormat: () =>
        moment
          .localeData(locale ?? 'es')
          // .locale(locale ?? 'es')
          // .tz(timeZone ?? 'Europe/Madrid')
          .longDateFormat('LL'),

      monthFormat: 'MMMM YYYY',
      // isOutsideRange: () => false,
    };

    return (
      <div
        className={classNames(
          {
            'border-critical-no-bg focus:border-critical-no-bg hover:border-critical-no-bg': hasError,
            'border-soft focus:border-hard hover:border-hard': !hasError,
            'text-primary': !readOnly,
            'cursor-not-allowed border-soft focus:border-hard hover:border-hard': readOnly,
          },
          'flex text-body-m focus:text-subtitle-m p-0.5 fill-primary relative rounded-md border'
        )}
        ref={wrapperRef}
        onClick={onMouseDownHandler}
        onMouseDown={onMouseDownHandler}
        onTouchStart={onMouseDownHandler}
        onKeyPress={onMouseDownHandler}
        role='button'
        tabIndex={0}
        data-testid={props['data-testid']}>
        <SingleDatePicker
          {...defaultProps}
          showClearDate={showClearDate}
          customCloseIcon={<IconCross className='w-3 h-3 text-primary' />}
          date={value ? value : null}
          focused={isFocused}
          onDateChange={handleDateChange}
          onFocusChange={onFocusChangeHandler}
          isOutsideRange={(day) => {
            if (minBookingDate) {
              if (day.isBefore(minBookingDate, 'days')) return true;
            }

            if (maxBookingDate) return day.isAfter(maxBookingDate, 'days');

            return false;
          }}
        />
      </div>
    );
  }
);
